/**
 * Comportamento dos blocos ZEPH na pagina de produto.
 *
 * O Horizon troca de variante buscando o HTML novo da secao e disparando
 * `shopify:product:select`; cada bloco nativo se atualiza a partir desse HTML.
 * Os blocos ZEPH seguem o mesmo contrato: tudo que depende da variante
 * carrega `data-zeph-live` e e substituido pela versao do HTML novo. Assim a
 * logica fica no Liquid, num lugar so, e o JS nao recalcula nada.
 */

const LIVE = '[data-zeph-live]';

document.addEventListener('shopify:product:select', (event) => {
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  if (!section || !event.promise) return;

  event.promise
    .then(({ detail }) => {
      if (!detail?.html) return;

      for (const element of section.querySelectorAll(LIVE)) {
        const fresh = detail.html.querySelector(`[data-zeph-live="${element.dataset.zephLive}"]`);
        if (fresh) element.replaceWith(fresh);
      }

      renderShipDates(section);
    })
    .catch(() => {});
});

/*
  Bolinhas de cor: acionam o radio do seletor nativo, que continua dono do
  preco, da galeria e da URL. O estado ativo muda na hora para o toque
  responder sem esperar a rede; o HTML novo confirma logo depois.
*/
document.addEventListener('click', (event) => {
  const dot = event.target instanceof Element ? event.target.closest('[data-zeph-option-value]') : null;
  if (!dot) return;

  const section = dot.closest('.shopify-section');
  const input = section?.querySelector(
    `variant-picker input[data-option-value-id="${dot.dataset.zephOptionValue}"]`
  );
  if (!(input instanceof HTMLInputElement) || input.checked) return;

  for (const sibling of dot.parentElement?.children ?? []) {
    sibling.classList.toggle('is-active', sibling === dot);
    sibling.setAttribute('aria-checked', String(sibling === dot));
  }

  input.click();
});

/*
  Data de envio calculada no navegador: a pagina fica em cache na CDN da
  Shopify, entao uma data gerada no servidor pode chegar velha. Pula sabado e
  domingo.
*/
function renderShipDates(root = document) {
  const format = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  for (const element of root.querySelectorAll('[data-zeph-ship-days]')) {
    const date = new Date();
    let remaining = Number(element.getAttribute('data-zeph-ship-days')) || 0;

    while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) remaining -= 1;
    }

    element.textContent = format.format(date);
  }
}

renderShipDates();

/*
  Sticky add to cart: em vez de adicionar direto, sobe a pagina ate o bundle
  do Kaching, para o cliente escolher a oferta (1 relogio, BOGO...) antes de
  comprar. O Horizon liga o clique do botao ao componente (on:click) com um
  listener de captura no document, registrado antes deste modulo; por isso
  este fica na captura do window, que roda antes de qualquer um no document.
  Produto sem bundle (pulseiras, oculos): sobe ate os botoes de compra.
*/
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.sticky-add-to-cart__button') : null;
    if (!button) return;

    const target =
      document.querySelector('.product-information .kaching-bundles') ||
      document.querySelector('.product-information [id$="__kaching"]') ||
      document.querySelector('.product-information .variant-picker, .product-information variant-picker') ||
      document.querySelector('.product-information [ref="addToCartButton"]');
    if (!target) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const header = document.querySelector('#header-component');
    const offset = (header ? header.getBoundingClientRect().height : 60) + 16;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* No desktop (990px+) o Horizon trava html/body e rola a pagina dentro de
       .page-wrapper; no mobile quem rola e a janela. Rola quem estiver
       rolando de fato. */
    const wrapper = document.querySelector('.page-wrapper');
    const wrapperScrolls =
      wrapper && /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY) && wrapper.scrollHeight > wrapper.clientHeight + 1;
    const scroller = wrapperScrolls ? wrapper : window;
    const current = wrapperScrolls ? wrapper.scrollTop : window.scrollY;
    const originTop = wrapperScrolls ? wrapper.getBoundingClientRect().top : 0;
    const top = target.getBoundingClientRect().top - originTop + current - offset;
    scroller.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });

    // Destaque curto quando a rolagem termina, para o olho achar a oferta.
    const flash = () => {
      target.classList.remove('zeph-bundle-flash');
      void target.offsetWidth;
      target.classList.add('zeph-bundle-flash');
      setTimeout(() => target.classList.remove('zeph-bundle-flash'), 1400);
    };
    if ('onscrollend' in window && !reduced) scroller.addEventListener('scrollend', flash, { once: true });
    else setTimeout(flash, reduced ? 0 : 500);
  },
  true
);

/*
  Setas na galeria do produto no celular. O Horizon so desenha setas no
  desktop; no celular deixa pontinhos brancos, que somem sobre as fotos
  claras da ZEPH. Duas setas finas nas bordas da foto chamam previous() e
  next() do proprio slideshow do tema. Depois da troca de variante o Horizon
  redesenha a galeria e leva as setas junto, entao elas sao recolocadas.
*/
const GALLERY_MOBILE = window.matchMedia('(max-width: 749px)');

function ensureGalleryArrows(root = document) {
  if (!GALLERY_MOBILE.matches) return;
  const slideshow = root.querySelector('media-gallery slideshow-component[ref="slideshow"]');
  const container = slideshow?.querySelector(':scope > slideshow-container');
  if (!slideshow || !container || container.querySelector('.zeph-gal-arrows')) return;
  if (container.querySelectorAll('slideshow-slide:not([hidden])').length < 2) return;

  const arrows = document.createElement('div');
  arrows.className = 'zeph-gal-arrows';
  arrows.innerHTML = `
    <button type="button" class="zeph-gal-arrow zeph-gal-arrow--prev" aria-label="Previous image">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>
    <button type="button" class="zeph-gal-arrow zeph-gal-arrow--next" aria-label="Next image">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>`;
  arrows.querySelector('.zeph-gal-arrow--prev').addEventListener('click', (event) => slideshow.previous(event));
  arrows.querySelector('.zeph-gal-arrow--next').addEventListener('click', (event) => slideshow.next(event));
  container.appendChild(arrows);
}

customElements.whenDefined('slideshow-component').then(() => ensureGalleryArrows());

document.addEventListener('shopify:product:select', (event) => {
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  if (!section || !event.promise) return;
  /* O Horizon redesenha a galeria no mesmo promise; espera um quadro para
     recolocar as setas depois dele. */
  event.promise.then(() => setTimeout(() => ensureGalleryArrows(section), 60)).catch(() => {});
});
